import { useEffect, useRef, useState } from "react";

/**
 * Animates a counter from 0 → target when the element enters the viewport.
 * Returns a ref to attach + the current displayed value.
 *
 * Supports plain numbers (15) and decimals (4.9) and large numbers (10000).
 */
export default function useCountUp(target, { duration = 1400, decimals = 0 } = {}) {
  const ref = useRef(null);
  const [value, setValue] = useState(0);
  const started = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting && !started.current) {
            started.current = true;
            const start = performance.now();
            const num = Number(target) || 0;
            const tick = (now) => {
              const t = Math.min(1, (now - start) / duration);
              const eased = 1 - Math.pow(1 - t, 3); // ease-out cubic
              const next = num * eased;
              setValue(decimals === 0 ? Math.floor(next) : Number(next.toFixed(decimals)));
              if (t < 1) requestAnimationFrame(tick);
              else setValue(num);
            };
            requestAnimationFrame(tick);
            io.disconnect();
          }
        });
      },
      { threshold: 0.4 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [target, duration, decimals]);

  return [ref, value];
}
